/**
 * Table data cell presentation for the scouting table
 *
 * Renders cell content styled according to column type — GPR as a
 * rectangular info badge (surface-info-subtle fill, needs-semantic-info-700 ink,
 * both dark-mode aware),
 * position as a muted chip with hover tooltip, watchlist names with
 * truncation and hover tooltip, country with flag, player name with a
 * three-dot action menu visible on row hover, and generic text for
 * other columns. Pixel-matched to the Figma spec: 50px row height,
 * 24px horizontal / 14px vertical padding, badge 8px/4px padding.
 * @module features/scouting/components/DataCell/DataCellView
 */

import React, { memo } from "react";
import { Platform } from "react-native";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Image } from "@/components/atoms/Image";
import { Pressable } from "@/components/atoms/Pressable";
import { TableData } from "@/components/atoms/Table";
import { Text } from "@/components/atoms/Text";
import {
  Tooltip,
  TooltipContent,
  TooltipText,
} from "@/components/atoms/Tooltip";

import { deriveWatchlistDisplayFromString } from "@/features/scouting/utils/deriveWatchlistDisplay";

const IS_WEB = Platform.OS === "web";

/** Em dash for missing values */
const EM_DASH = "\u2014";

/** Em dash text element reused across column renderers */
const emDashElement = (
  <Text
    variant="unstyled"
    className="text-title-sm leading-6 text-content-subtle"
  >
    {EM_DASH}
  </Text>
);

/**
 * Muted text style for em dash placeholders, normal style for real values
 * @param value - Display text to check against em dash
 */
const getTextClassName = (value: string): string =>
  value === EM_DASH
    ? "text-title-sm leading-6 text-content-subtle"
    : "text-title-sm leading-6 text-needs-semantic-typography-800";

/**
 * The GPR badge — the ONE implementation of this readout in the product.
 * @remarks
 * Exported because the pipeline board's Table renders the same figure and
 * Raniel ruled on 2026-08-07 that it must use this component as its reference
 * ("usa a referência desse componente do GPR como está implementado na tabela
 * de scouting"). Sharing the function rather than copying its classes is what
 * makes "identical" survive the next restyle of either table.
 *
 * Worth stating plainly because it has been assumed otherwise: **there is no
 * banding, no threshold and no value-to-colour scale here.** Every GPR renders
 * on the same flat `surface-info-subtle`/`needs-semantic-info-700` pairing
 * regardless of value. The
 * badge also rounds to a whole number, so 78.5 reads `79`.
 *
 * `alignSelf` is the only axis the two callers differ on: a cell is a column
 * flexbox, so the badge must declare its own cross-axis placement or it
 * stretches. Scouting's GPR column is numeric and right-aligned (`end`, the
 * default, which keeps this call site byte-identical); the pipeline table
 * centres its GPR column.
 * @param gpr - GPR numeric value
 * @param alignSelf - Cross-axis placement within the owning cell
 * @returns The badge element
 */
export const renderGprBadge = (
  gpr: number,
  alignSelf: "end" | "center" = "end"
) => (
  <Box
    className={`web:inline-flex min-w-10 items-center rounded-none bg-surface-info-subtle px-2 py-1 align-top ${
      alignSelf === "center" ? "self-center" : "self-end"
    }`}
  >
    <Text variant="micro" className="text-center text-needs-semantic-info-700">
      {gpr.toFixed(0)}
    </Text>
  </Box>
);

/**
 * Renders GPR badge or em dash for null values
 * @param gpr - GPR numeric value
 */
const renderGpr = (gpr: number | null) =>
  gpr !== null ? renderGprBadge(gpr) : emDashElement;

/**
 * Creates a tooltip trigger for position chips — renders the position
 * text inside a muted badge with truncation via max-width
 * @param displayValue - Comma-joined position abbreviations
 */
function createPositionTrigger(displayValue: string) {
  return function PositionTriggerRender(triggerProps: Record<string, unknown>) {
    return (
      <Box
        {...triggerProps}
        className="web:inline-flex max-w-full self-start rounded-none bg-surface-subtle px-2 py-1 align-top"
      >
        <Text variant="caption" isTruncated className="text-content-primary">
          {displayValue}
        </Text>
      </Box>
    );
  };
}

/**
 * Renders position badges with truncation and tooltip on web.
 * Shows all positions (primary + secondary) comma-joined.
 * On web, hovering over truncated text shows a tooltip with full list.
 * On native, Tooltip is web-only so positions display truncated without tooltip.
 * @param displayValue - Comma-joined position abbreviations or em dash
 */
const renderPosition = (displayValue: string) =>
  displayValue !== EM_DASH ? (
    IS_WEB ? (
      <Tooltip placement="top" trigger={createPositionTrigger(displayValue)}>
        <TooltipContent>
          <TooltipText>{displayValue}</TooltipText>
        </TooltipContent>
      </Tooltip>
    ) : (
      <Box className="max-w-full self-start rounded-none bg-surface-subtle px-2 py-1">
        <Text variant="caption" isTruncated className="text-content-primary">
          {displayValue}
        </Text>
      </Box>
    )
  ) : (
    emDashElement
  );

/**
 * Renders country name with flag icon, or em dash when unavailable
 * @param displayValue - Country name or em dash
 * @param flagUrl - CDN URL for the country flag SVG
 */
const renderCountry = (displayValue: string, flagUrl: string | null) =>
  displayValue !== EM_DASH && flagUrl ? (
    <Stack direction="horizontal" className="items-center gap-2">
      <Image source={{ uri: flagUrl }} className="h-4 w-5" alt={displayValue} />
      <Text variant="unstyled" className={getTextClassName(displayValue)}>
        {displayValue}
      </Text>
    </Stack>
  ) : (
    <Text variant="unstyled" className={getTextClassName(displayValue)}>
      {displayValue}
    </Text>
  );

/**
 * Renders a single watchlist chip. Either the list name (when the player
 * sits in exactly one list) or an aggregate "N lists" label (when in two or
 * more). The chip uses the same `bg-surface-info-subtle` / `text-needs-semantic-info-700` palette as
 * the GPR badge for visual consistency; on web, hover lifts the outline to
 * `info-300` per Figma 40000732:28348 (the default `border-transparent`
 * keeps the box the same size in both states — no layout shift).
 * No native browser/Tooltip surface — the full set of names lives in the
 * `displayValue` string and downstream surfaces (e.g. the watchlist
 * popover) handle that disclosure instead.
 * @param label - Visible chip text
 */
const renderWatchlistChip = (label: string) => (
  <Box className="web:inline-flex web:cursor-pointer web:hover:border-needs-semantic-info-300 min-w-0 max-w-full shrink items-center self-start rounded-none border border-transparent bg-surface-info-subtle px-2 py-1">
    <Text
      variant="micro"
      isTruncated
      className="web:truncate min-w-0 shrink text-needs-semantic-info-700"
    >
      {label}
    </Text>
  </Box>
);

const renderWatchlistNames = (displayValue: string, watchlistCount: number) => {
  if (displayValue === EM_DASH) {
    return emDashElement;
  }
  const { firstName } = deriveWatchlistDisplayFromString(
    displayValue,
    watchlistCount
  );
  const label = watchlistCount > 1 ? `${watchlistCount} lists` : firstName;
  return renderWatchlistChip(label);
};

/**
 * Renders the player name. When `onPress` is provided, the name becomes the
 * sole navigation handle for the row (web: cursor pointer + hover underline,
 * native: pressable) — the row body itself no longer routes the click so that
 * other cell controls (watchlist chips, kebab) can be tapped without bouncing
 * to the player page. The per-row action menu lives in the trailing
 * right-pinned `rowActions` column (SE-5012 / RowActionsCell).
 * @param displayValue - Player name or em dash
 * @param onPress - Optional navigation handler; gates the interactive style
 * @param leadingContent - Optional content before the player name
 */
const renderPlayerName = (
  displayValue: string,
  onPress?: () => void,
  leadingContent?: React.ReactNode
) => {
  const label = displayValue || EM_DASH;
  const name = !onPress ? (
    <Text
      variant="unstyled"
      className={`${getTextClassName(label)} web:truncate`}
    >
      {label}
    </Text>
  ) : (
    <Pressable
      onPress={onPress}
      accessibilityRole="link"
      accessibilityLabel={label}
      className="group/name web:cursor-pointer"
    >
      <Text
        variant="unstyled"
        className={`${getTextClassName(label)} web:truncate web:group-hover/name:underline font-body`}
      >
        {label}
      </Text>
    </Pressable>
  );
  return leadingContent ? (
    <Stack direction="horizontal" className="min-w-0 items-center">
      {leadingContent}
      <Box className="min-w-0 flex-1 px-6">{name}</Box>
    </Stack>
  ) : (
    name
  );
};

/** Props for DataCellView */
interface DataCellViewProps {
  /** Column identifier to determine rendering style */
  readonly columnId: string;
  /** Display value as string */
  readonly displayValue: string;
  /** GPR numeric value for badge rendering (null if not GPR column) */
  readonly gpr: number | null;
  /** Country flag SVG URL from CDN — null when nationality unavailable */
  readonly countryFlagUrl: string | null;
  /** Inline style for width, minWidth, and pinning */
  readonly cellStyle: Record<string, string | number>;
  /** Whether this cell belongs to a left-pinned column */
  readonly isPinned: boolean;
  /** Whether this column contains numeric data — right-aligns content */
  readonly isNumeric: boolean;
  /** Total number of watchlists for this player — used by Lists column */
  readonly watchlistCount: number;
  /**
   * Navigation handler for the playerName cell. Forwarded by PlayerRowView
   * so clicking the player's name (and only that cell) opens the player page.
   */
  readonly onPlayerNamePress?: () => void;
  /** Rank and preference controls rendered before a player name. */
  readonly leadingContent?: React.ReactNode;
}

/** Lookup table mapping column IDs to their render functions */
const COLUMN_RENDERERS: Record<
  string,
  (props: DataCellViewProps) => React.ReactNode
> = {
  gpr: (props: DataCellViewProps) => renderGpr(props.gpr),
  position: (props: DataCellViewProps) => renderPosition(props.displayValue),
  country: (props: DataCellViewProps) =>
    renderCountry(props.displayValue, props.countryFlagUrl),
  playerName: (props: DataCellViewProps) =>
    renderPlayerName(
      props.displayValue,
      props.onPlayerNamePress,
      props.leadingContent
    ),
  watchlistNames: (props: DataCellViewProps) =>
    renderWatchlistNames(props.displayValue, props.watchlistCount),
  addColumn: () => null,
};

/**
 * Default text renderer for columns without a specific renderer
 * @param props - Data cell props with displayValue for generic text output
 */
const renderDefaultText = (props: DataCellViewProps): React.ReactNode => (
  <Text
    variant="unstyled"
    isTruncated
    className={`${getTextClassName(props.displayValue)} web:truncate${
      props.isNumeric ? " w-full text-right" : ""
    }`}
  >
    {props.displayValue}
  </Text>
);

/**
 * Resolves cell content using the column renderer lookup or generic text fallback
 * @param props - Data cell props containing columnId and display values
 */
const resolveCellContent = (props: DataCellViewProps): React.ReactNode =>
  (COLUMN_RENDERERS[props.columnId] ?? renderDefaultText)(props);

/**
 * Sticky-pinning + numeric-alignment cell style.
 * design-system: dynamic-style waiver — data-driven value (caller cellStyle + pin/numeric conditionals)
 * @param props - DataCell view props
 */
const dataCellStyle = (props: DataCellViewProps) => ({
  ...props.cellStyle,
  overflow: "hidden" as const,
  ...(props.isNumeric ? { textAlign: "right" as const } : {}),
  ...(props.isPinned ? { boxShadow: "-4px 0 4px -4px gray inset" } : {}),
});

/**
 * Renders a table data cell with column-specific styling.
 * Uses a lookup table to select the appropriate renderer by column ID,
 * falling back to generic text for unrecognized columns.
 * @param props - Component props with columnId, displayValue, and styling
 */
const DataCellView = (props: DataCellViewProps) => (
  <TableData
    className={`!h-12 border-b border-outline-default bg-surface-base ${props.leadingContent ? "!px-0" : "!px-6"} !py-3 align-middle font-body group-hover:bg-surface-raised`}
    // eslint-disable-next-line no-restricted-syntax -- data-driven cellStyle (arbitrary column width/pin left offset) + pinned inset boxShadow; no closed token, TableData is a bare re-export atom (no UNSAFE_style prop)
    style={dataCellStyle(props)}
  >
    {resolveCellContent(props)}
  </TableData>
);

DataCellView.displayName = "DataCellView";

export default memo(DataCellView);
